import { Track } from "@/src/lib/tracking";
import { FC, Fragment, memo, useEffect, useState } from "react";
import { cn } from "utils";
import { Popover } from "antd";
import { Icon } from "@ospweb/sdk-ui";
import { ProductLevelType } from "../constants";
import { SubscriptionListProps, PayChannelMap } from "./types";
import Translations from "@translate/components/Member/CurrentPlan/ManageBillingModal/SubscriptionList";
import { I18nUtil } from "@utils/i18n";
import { PayChannel } from "@features/api/memberApi";
import { ShowOnPcOrMobile } from "@components/ShowOnPcOrMobile";

const I18n = I18nUtil.createScoped(Translations);

/** 根据支付渠道返回取消按钮文案 */
function getCancelButtonLabel(payChannel: string): string {
  switch (payChannel) {
    case PayChannel.STRIPE:
      return I18n.t("Manage");
    case PayChannel.PAYPAL:
      // PayPal 无商户代建门户，后端 /payment/plans 注入其账户 autopay 管理页链接
      // （rel=customer_session_url，域名随环境在后端切换），前端与 Stripe 统一消费 links
      return I18n.t("Manage");
    case PayChannel.APPLE_IAP:
      return I18n.t("Manage on Apple");
    case PayChannel.GOOGLE_IAP:
      return I18n.t("Manage on google");
    default:
      return I18n.t("Manage");
  }
}

/**
 * 订阅列表 UI 组件
 * 展示自动续费订阅列表：支付方式、计划名、下次付款日期，及取消按钮（Stripe / Apple / Google 文案区分）
 */
const SubscriptionListInner: FC<SubscriptionListProps> = ({
  items = [],
  onCancel,
  onManageSubscription,
}) => {
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    Track.view("member_subscription_list_view");
  }, []);

  if (!items.length) {
    return (
      <div className="flex flex-col gap-4 bg-background_solid_secondary w-full pt-6">
        <p className="body_medium_m text-content_tertiary">
          {I18n.t("No auto-renewing subscriptions")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full mt-6 md:mt-8 max-h-[412px] min-h-[240px] overflow-y-auto no-scrollbar">
      {items.map((item, index) => (
        <div
          key={`${item.pay_channel}-${item.product_level ?? "unknown"}-${index}`}
          className="flex items-center justify-between w-full py-4 border-b border-border_white_transparent10 last:border-b-0 last:pb-0 first:pt-0"
        >
          <div className="flex flex-col gap-0.5 flex-1 min-w-0">
            <p className="title_l text-content_primary">
              {PayChannelMap[item.pay_channel as keyof typeof PayChannelMap]}
            </p>
            <p className="body_m text-content_tertiary">
              {(item.product_level
                ? ProductLevelType[item.product_level]
                : "—") || "—"}{" "}
              {I18n.t("· Next Payment:")}
              {item.nextPaymentDate}
            </p>
          </div>
          <ShowOnPcOrMobile showMode="mobile">
            <Popover
              content={
                <Fragment>
                  {item.canCancel && (
                    <button
                      data-openreplay-label="member_subscriptionlist_click"
                      type="button"
                      onClick={() => onManageSubscription?.(item)}
                      disabled={
                        (item.pay_channel === PayChannel.STRIPE ||
                          item.pay_channel === PayChannel.PAYPAL) &&
                        !item.links
                      }
                      className={cn(
                        "body_medium_m w-full p-2 gap-2 flex flex-row items-center text-content_primary disabled:text-content_quaternary group",
                      )}
                    >
                      <Icon
                        name="customize-regular"
                        size={16}
                        className="text-content_primary hover:opacity-60 transition-opacity group-disabled:text-content_quaternary"
                      />
                      {getCancelButtonLabel(item.pay_channel)}
                    </button>
                  )}
                  <button
                    data-openreplay-label="member_subscriptionlist_click"
                    type="button"
                    onClick={() => onCancel?.(item)}
                    disabled={
                      (item.pay_channel === PayChannel.STRIPE ||
                        item.pay_channel === PayChannel.PAYPAL) &&
                      !item.links
                    }
                    className={cn(
                      "body_medium_m w-full p-2 gap-2 flex flex-row items-center text-content_primary disabled:text-content_quaternary group",
                      { hidden: item.status !== "ACTIVE" },
                    )}
                  >
                    <Icon
                      name="close"
                      size={14}
                      className="text-content_primary !inline-flex border rounded-full size-3.75 items-center justify-center border-content_primary group-disabled:border-content_quaternary group-disabled:text-content_quaternary"
                    />
                    {I18n.t("Cancel Subscription")}
                  </button>
                </Fragment>
              }
              trigger="click"
              placement="bottomRight"
              arrow={false}
              open={openId === item.id}
              onOpenChange={(open) => {
                if (open) {
                  setOpenId(item.id);
                } else {
                  setOpenId(null);
                }
              }}
              classNames={{
                root: "rounded-2 border-solid border border-border_solid insetShadowMd",
                body: "!bg-background_solid_neutral_secondary",
              }}
              styles={{
                root: {
                  padding: 0,
                },
                body: {
                  padding: 8,
                },
              }}
            >
              <button
                data-openreplay-label="member_moreactions_click"
                id="btn-more-actions"
                className="relative shrink-0 rounded-full flex items-center justify-center"
                aria-label="More actions"
              >
                <Icon
                  name="dots-horizontal-rounded"
                  size={20}
                  className="text-content_primary hover:opacity-60 transition-opacity"
                />
              </button>
            </Popover>
          </ShowOnPcOrMobile>
          <ShowOnPcOrMobile showMode="pc">
            <div className="flex items-start shrink-0 mr-[1px]">
              <button
                data-openreplay-label="member_subscriptionlist_click"
                type="button"
                onClick={() => onManageSubscription?.(item)}
                disabled={
                  !item.canCancel ||
                  ((item.pay_channel === PayChannel.STRIPE ||
                    item.pay_channel === PayChannel.PAYPAL) &&
                    !item.links)
                }
                className={cn(
                  "button_s xs_button button secondary_button min-w-20 md:min-w-28",
                )}
              >
                {getCancelButtonLabel(item.pay_channel)}
              </button>
            </div>
          </ShowOnPcOrMobile>
        </div>
      ))}
    </div>
  );
};

export const SubscriptionList = memo(SubscriptionListInner);
